import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Profile, UserRole } from '../../types';
import { logAction, logUsageEvent } from '../lib/logger';
import { X, User, Mail, Lock, Briefcase, Phone, Building2, Loader2, Plus, Users, Check } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { api } from '../services/api';
import { DEPARTAMENTOS, DEPT_COLOR } from '../lib/departamentos';

interface CreateUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    initialData?: Partial<{
        fullName: string;
        email: string;
        role: UserRole;
        jobTitle: string;
        departamentos: string[];
        whatsapp: string;
        parceiro_id: string;
    }>;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ isOpen, onClose, onSuccess, initialData }) => {
    const { profile } = useAuth();
    const { addToast } = useToast();

    const [creating, setCreating] = useState(false);
    const [users, setUsers] = useState<Profile[]>([]);
    const [deptOpen, setDeptOpen] = useState(false);
    const [newUser, setNewUser] = useState({
        fullName: '',
        email: '',
        password: '',
        role: 'vendedor' as UserRole,
        jobTitle: '',
        departamentos: [] as string[],
        whatsapp: '',
        parceiro_id: '',
        email_pessoal: '',
        cpf: ''
    });

    useEffect(() => {
        if (isOpen) {
            loadUsers();
            setDeptOpen(false);
            // Reset and apply initialData/defaults
            const defaultRole = profile?.role === 'parceiro' ? 'vendedor_parceiro' as UserRole :
                profile?.role === 'gerente_parcerias' ? 'parceiro' as UserRole :
                    profile?.role === 'gerente' ? 'vendedor' as UserRole : 'vendedor' as UserRole;

            setNewUser({
                fullName: initialData?.fullName || '',
                email: initialData?.email || '',
                password: '',
                role: initialData?.role || defaultRole,
                jobTitle: initialData?.jobTitle || '',
                departamentos: initialData?.departamentos || [],
                whatsapp: initialData?.whatsapp || '',
                parceiro_id: initialData?.parceiro_id || '',
                email_pessoal: '',
                cpf: ''
            });
        }
    }, [isOpen, initialData, profile?.role]);

    const toggleDept = (dept: string) => {
        setNewUser(prev => ({
            ...prev,
            departamentos: prev.departamentos.includes(dept)
                ? prev.departamentos.filter(d => d !== dept)
                : [...prev.departamentos, dept]
        }));
    };

    const loadUsers = async () => {
        try {
            const data = await api.auth.listProfiles();
            setUsers(data);
        } catch (error) {
            console.error('Error loading users:', error);
        }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 11) value = value.slice(0, 11);

        let formatted = value;
        if (value.length > 2) {
            formatted = `(${value.slice(0, 2)}) ${value.slice(2)}`;
        }
        if (value.length > 7) {
            formatted = `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7)}`;
        }

        setNewUser({ ...newUser, whatsapp: formatted });
    };

    const maskCPF = (value: string) => {
        return value
            .replace(/\D/g, '')
            .slice(0, 11)
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    };

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newUser.departamentos.length === 0) {
            addToast('Selecione pelo menos um departamento.', 'error');
            return;
        }
        setCreating(true);
        try {
            const payload = {
                ...newUser,
                // legacy field: first selected dept
                departamento: newUser.departamentos[0] || '',
            };
            const { data, error } = await supabase.functions.invoke('create-user', {
                body: payload
            });

            if (error) {
                let errorMsg = error.message;
                try {
                    if (error.context && typeof error.context.json === 'function') {
                        const body = await error.context.json();
                        if (body?.error) errorMsg = body.error;
                    }
                } catch (_) { }
                throw new Error(errorMsg);
            }

            if (data?.error) {
                throw new Error(data.error);
            }

            addToast('Usuário criado com sucesso! Ele já pode fazer login.', 'success');

            await logAction(profile?.id, 'CREATE_USER', {
                name: newUser.fullName,
                email: newUser.email,
                role: newUser.role
            });
            logUsageEvent({ userId: profile?.id, modulo: 'config', funcionalidade: 'usuarios', acao: 'criar_usuario', origem: 'acao' });

            if (onSuccess) onSuccess();
            onClose();
        } catch (error: any) {
            console.error('Error creating user:', error);
            addToast('Erro ao criar usuário: ' + (error.message || 'Erro desconhecido'), 'error', 7000);
        } finally {
            setCreating(false);
        }
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden transform transition-all">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
                    <h3 className="font-bold text-lg text-gray-800 dark:text-white">Novo Usuário</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleCreateUser} className="p-6 space-y-4 max-h-[calc(100vh-120px)] overflow-y-auto">
                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nome Completo</label>
                        <div className="relative">
                            <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                required
                                type="text"
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                                placeholder="Ex: Ana Silva"
                                value={newUser.fullName}
                                onChange={e => setNewUser({ ...newUser, fullName: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Email Corporativo</label>
                        <div className="relative">
                            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                required
                                type="email"
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                                placeholder="nome@vigorenergy.com.br"
                                value={newUser.email}
                                onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Cargo / Função</label>
                        <div className="relative">
                            <Briefcase size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                                placeholder="Ex: Gerente Comercial"
                                value={newUser.jobTitle}
                                onChange={e => setNewUser({ ...newUser, jobTitle: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Departamentos <span className="text-red-500">*</span></label>
                        <div className="relative">
                            {/* Trigger button */}
                            <button
                                type="button"
                                onClick={() => setDeptOpen(o => !o)}
                                className="w-full flex items-center gap-2 pl-3 pr-4 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-left"
                            >
                                <Building2 size={16} className="text-gray-400 shrink-0" />
                                <span className="flex-1 truncate text-gray-500 dark:text-gray-400">
                                    {newUser.departamentos.length === 0
                                        ? 'Selecione departamento(s)...'
                                        : newUser.departamentos.join(', ')}
                                </span>
                            </button>
                            {/* Dropdown checkboxes */}
                            {deptOpen && (
                                <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl max-h-52 overflow-y-auto">
                                    {DEPARTAMENTOS.map(dept => {
                                        const checked = newUser.departamentos.includes(dept);
                                        return (
                                            <label
                                                key={dept}
                                                className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                            >
                                                <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${checked ? 'bg-primary border-primary' : 'border-gray-300 dark:border-gray-500'
                                                    }`}>
                                                    {checked && <Check size={10} className="text-white" strokeWidth={3} />}
                                                </div>
                                                <input type="checkbox" className="hidden" checked={checked} onChange={() => toggleDept(dept)} />
                                                <span className="text-sm text-gray-700 dark:text-gray-200">{dept}</span>
                                            </label>
                                        );
                                    })}
                                    <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700">
                                        <button
                                            type="button"
                                            onClick={() => setDeptOpen(false)}
                                            className="w-full text-center text-xs font-bold text-primary hover:text-primary/80 py-1"
                                        >
                                            Confirmar
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* Selected badges */}
                        {newUser.departamentos.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 pt-1">
                                {newUser.departamentos.map(d => (
                                    <span key={d} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${DEPT_COLOR[d] || 'bg-gray-100 text-gray-700'}`}>
                                        {d}
                                        <button type="button" onClick={() => toggleDept(d)} className="hover:opacity-70">×</button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">WhatsApp / Celular</label>
                        <div className="relative">
                            <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                                placeholder="(62) 99115-3243"
                                value={newUser.whatsapp}
                                onChange={handlePhoneChange}
                                maxLength={15}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">E-mail Pessoal <span className="text-gray-400 font-normal">(opcional)</span></label>
                            <div className="relative">
                                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="email"
                                    className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                                    placeholder="nome@gmail.com"
                                    value={newUser.email_pessoal}
                                    onChange={e => setNewUser({ ...newUser, email_pessoal: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">CPF <span className="text-gray-400 font-normal">(opcional)</span></label>
                            <div className="relative">
                                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                                    placeholder="000.000.000-00"
                                    value={newUser.cpf}
                                    onChange={e => setNewUser({ ...newUser, cpf: maskCPF(e.target.value) })}
                                    maxLength={14}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Senha Inicial</label>
                        <div className="relative">
                            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                required
                                type="text"
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                                placeholder="Mínimo 6 caracteres"
                                value={newUser.password}
                                onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Permissão</label>
                        <select
                            className="w-full p-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none disabled:bg-gray-100 dark:disabled:bg-gray-900/50"
                            value={newUser.role}
                            onChange={e => setNewUser({ ...newUser, role: e.target.value as UserRole })}
                            disabled={profile?.role === 'parceiro'}
                        >
                            {profile?.role === 'parceiro' ? (
                                <option value="vendedor_parceiro">Vendedor Parceiro</option>
                            ) : profile?.role === 'gerente_parcerias' ? (
                                <>
                                    <option value="parceiro">Parceiro</option>
                                    <option value="vendedor_parceiro">Vendedor Parceiro</option>
                                </>
                            ) : profile?.role === 'gerente' ? (
                                <>
                                    <option value="vendedor">Vendedor</option>
                                    <option value="parceiro">Parceiro</option>
                                    <option value="vendedor_parceiro">Vendedor Parceiro</option>
                                    <option value="motoboy">Motoboy (Operador de Campo)</option>
                                </>
                            ) : (
                                <>
                                    <option value="vendedor">Vendedor</option>
                                    <option value="parceiro">Parceiro</option>
                                    <option value="vendedor_parceiro">Vendedor Parceiro</option>
                                    <option value="analista">Analista</option>
                                    <option value="motoboy">Motoboy (Operador de Campo)</option>
                                    <option value="gerente">Gerente Comercial</option>
                                    <option value="adm">Administrador</option>
                                    <optgroup label="── Empresas Parceiras ──">
                                        <option value="empresa_parceira_admin">EP — Administrador</option>
                                        <option value="empresa_parceira_operador">EP — Operador</option>
                                    </optgroup>
                                </>
                            )}
                        </select>
                    </div>

                    {newUser.role === 'vendedor_parceiro' && profile?.role !== 'parceiro' && (
                        <div className="space-y-1 animate-fade-in">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Vincular ao Responsável</label>
                            <div className="relative">
                                <Users size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <select
                                    className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                                    value={newUser.parceiro_id || ''}
                                    onChange={e => setNewUser({ ...newUser, parceiro_id: e.target.value })}
                                    required
                                >
                                    <option value="">Selecione...</option>
                                    {profile?.role === 'gerente_parcerias' && (
                                        <option value={profile.id}>🔷 Eu mesmo (Gerente de Parcerias)</option>
                                    )}
                                    {users.filter(u => u.role === 'parceiro').map(p => (
                                        <option key={p.id} value={p.id}>👤 {p.full_name} (Parceiro)</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    <div className="pt-4 flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={creating}
                            className="flex-1 py-2.5 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
                        >
                            {creating ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                            Criar Usuário
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default CreateUserModal;
